// /pages/HR/components/filters/CategoryFilter.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './CategoryFilter.css';

// Default HR categories with metadata - already outside component
const DEFAULT_CATEGORIES = [
  { id: 'employee_leave', name: 'Employee Leave', count: 223, color: '#D01FD3', group: 'Time Off' },
  { id: 'payroll', name: 'Payroll Request', count: 253, color: '#F46B02', group: 'Compensation' },
  { id: 'benefits', name: 'Benefits Request', count: 103, color: '#8B5CF6', group: 'Benefits' },
  { id: 'employee_info', name: 'Employee Information Change', count: 123, color: '#10B981', group: 'Employee Data' },
  { id: 'verification', name: 'Employment Verification', count: 223, color: '#3B82F6', group: 'Employee Data' },
  { id: 'new_hire', name: 'New Hire Setup', count: 65, color: '#EF4444', group: 'Onboarding' },
  { id: 'termination', name: 'Termination', count: 36, color: '#F59E0B', group: 'Offboarding' },
  { id: 'performance', name: 'Performance Review', count: 54, color: '#6B7280', group: 'Performance' },
  { id: 'training', name: 'Training Request', count: 50, color: '#EC4899', group: 'Development' },
  { id: 'complaint', name: 'HR Complaint/Concern', count: 35, color: '#14B8A6', group: 'Employee Relations' },
  { id: 'documentation', name: 'Documentation Request', count: 30, color: '#8B5CF6', group: 'Employee Data' },
  { id: 'other', name: 'Other', count: 19, color: '#6B7280', group: 'Other' },
];

/**
 * Category Filter Component
 * Multi-select dropdown for filtering by categories
 */
const CategoryFilter = React.memo(({
  categories = [],
  selectedCategories = [],
  onCategoryChange = () => {},
  placeholder = 'Select categories',
  searchable = true,
  multiSelect = true,
  grouped = false,
  showCounts = true,
  showColors = true,
  className = '',
  disabled = false,
  clearable = true,
  maxHeight = 400,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(selectedCategories);
  const [categoryData, setCategoryData] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Initialize category data
  useEffect(() => {
    if (categories.length > 0) {
      setCategoryData(categories);
    } else {
      setCategoryData(DEFAULT_CATEGORIES);
    }
    
    // Expand all groups by default if grouped
    if (grouped) {
      const groups = new Set(DEFAULT_CATEGORIES.map(cat => cat.group).filter(Boolean));
      setExpandedGroups(groups);
    }
  }, [categories, grouped]);

  // Filter categories based on search - memoized
  const filterCategories = useCallback((categories) => {
    if (!searchTerm) return categories;
    
    const searchLower = searchTerm.toLowerCase();
    return categories.filter(category =>
      category.name.toLowerCase().includes(searchLower) ||
      category.id.toLowerCase().includes(searchLower) ||
      (category.group && category.group.toLowerCase().includes(searchLower))
    );
  }, [searchTerm]);

  // Group categories if needed - memoized
  const groupedCategories = useMemo(() => {
    if (!grouped) return { '': categoryData };
    
    return categoryData.reduce((groups, category) => {
      const group = category.group || 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(category);
      return groups;
    }, {});
  }, [grouped, categoryData]);

  // Handle category selection - memoized
  const handleCategoryToggle = useCallback((categoryId) => {
    setSelected(prevSelected => {
      let newSelected;
      
      if (multiSelect) {
        if (prevSelected.includes(categoryId)) {
          newSelected = prevSelected.filter(id => id !== categoryId);
        } else {
          newSelected = [...prevSelected, categoryId];
        }
      } else {
        newSelected = prevSelected.includes(categoryId) ? [] : [categoryId];
        setIsOpen(false);
      }
      
      onCategoryChange(newSelected);
      return newSelected;
    });
  }, [multiSelect, onCategoryChange]);

  // Handle group toggle - memoized
  const handleGroupToggle = useCallback((groupName) => {
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(groupName)) {
        newExpanded.delete(groupName);
      } else {
        newExpanded.add(groupName);
      }
      return newExpanded;
    });
  }, []);

  // Select/Deselect all in group - memoized
  const handleGroupSelectAll = useCallback((groupCategories) => {
    const groupIds = groupCategories.map(cat => cat.id);
    const allSelected = groupIds.every(id => selected.includes(id));
    
    let newSelected;
    if (allSelected) {
      newSelected = selected.filter(id => !groupIds.includes(id));
    } else {
      newSelected = [...new Set([...selected, ...groupIds])];
    }
    
    setSelected(newSelected);
    onCategoryChange(newSelected);
  }, [selected, onCategoryChange]);

  // Clear selection - memoized
  const handleClear = useCallback(() => {
    setSelected([]);
    setSearchTerm('');
    onCategoryChange([]);
  }, [onCategoryChange]);

  // Get display text - memoized
  const displayText = useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      const category = categoryData.find(cat => cat.id === selected[0]);
      return category ? category.name : selected[0];
    }
    return `${selected.length} categories selected`;
  }, [selected, categoryData, placeholder]);

  // Toggle dropdown - memoized
  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  // Handle search change - memoized
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Update local state when props change
  useEffect(() => {
    setSelected(selectedCategories);
  }, [selectedCategories]);

  return (
    <div 
      ref={dropdownRef}
      className={`category-filter ${className} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      {/* Filter Trigger */}
      <button
        className="category-trigger"
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span className="category-text">
          {displayText}
        </span>
        {selected.length > 0 && !disabled && (
          <span className="category-badge">{selected.length}</span>
        )}
        <span className="dropdown-arrow">▼</span>
      </button>

      {/* Clear Button */}
      {clearable && selected.length > 0 && !disabled && (
        <button
          className="category-clear"
          onClick={handleClear}
          title="Clear category filter"
        >
          ✕
        </button>
      )}

      {/* Dropdown Content */}
      {isOpen && (
        <div 
          className="category-dropdown"
          style={{ maxHeight }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="category-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          )}

          {/* Category List */}
          <div className="category-list">
            {Object.entries(groupedCategories).map(([groupName, categories]) => {
              const filteredCats = filterCategories(categories);
              
              if (filteredCats.length === 0) return null;
              
              return (
                <div key={groupName} className="category-group">
                  {grouped && groupName && (
                    <div className="group-header">
                      <button
                        className="group-toggle"
                        onClick={() => handleGroupToggle(groupName)}
                      >
                        <span className="toggle-icon">
                          {expandedGroups.has(groupName) ? '▼' : '▶'}
                        </span>
                        <span className="group-name">{groupName}</span>
                        <span className="group-count">({filteredCats.length})</span>
                      </button>
                      {multiSelect && (
                        <button
                          className="group-select-all"
                          onClick={() => handleGroupSelectAll(filteredCats)}
                        >
                          Select All
                        </button>
                      )}
                    </div>
                  )}
                  
                  {(!grouped || expandedGroups.has(groupName)) && (
                    <div className="group-categories">
                      {filteredCats.map(category => (
                        <label
                          key={category.id}
                          className={`category-item ${selected.includes(category.id) ? 'selected' : ''}`}
                        >
                          <input
                            type={multiSelect ? "checkbox" : "radio"}
                            checked={selected.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                            className="category-checkbox"
                          />
                          {showColors && category.color && (
                            <span 
                              className="category-color"
                              style={{ backgroundColor: category.color }}
                            />
                          )}
                          <span className="category-name">{category.name}</span>
                          {showCounts && category.count > 0 && (
                            <span className="category-count">{category.count}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {Object.values(groupedCategories).every(cats => filterCategories(cats).length === 0) && (
              <div className="no-results">
                No categories found matching "{searchTerm}"
              </div>
            )}
          </div>

          {/* Summary Footer */}
          {multiSelect && selected.length > 0 && (
            <div className="category-footer">
              <span className="selection-summary">
                {selected.length} of {categoryData.length} selected
              </span>
              <button
                className="clear-selection"
                onClick={handleClear}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

CategoryFilter.displayName = 'CategoryFilter';

export default CategoryFilter;